import type React from 'react';
import { isPrintableKey } from '../../printableKey';

type UseProductGridKeyboardParams = {
    /** A redirected keystroke: append `key` to search and focus it. */
    onPrintableKey: (key: string) => void;
};

/**
 * The product grid turns typeahead off (bos-seller#55): a letter or digit
 * typed while it has focus goes into search instead of jumping to a matching
 * tile, so a scan or a search the seller starts while browsing is never
 * lost. Which keys count is `isPrintableKey`, the rule the cart shares —
 * Shift included, so a Georgian Shift letter or a scanner's capital lands in
 * search too. Returns a capture-phase `keydown` handler for the grid's
 * wrapping element — capture, so it sees the keystroke before the list box's
 * own native typeahead does, and can stop it from ever getting there. Every
 * other key (arrows, Home/End, Enter) is left alone.
 */
export const useProductGridKeyboard = ({
    onPrintableKey,
}: UseProductGridKeyboardParams) => {
    const onGridKeyDownCapture = (event: React.KeyboardEvent) => {
        if (!isPrintableKey(event.nativeEvent)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        onPrintableKey(event.key);
    };

    return { onGridKeyDownCapture };
};
